import { useOverlayPosition } from '@react-native-aria/overlays';
import { Placement } from '@react-types/overlays';
import { View } from 'react-native';
import { styled } from 'dripsy';
import React, { useRef, RefObject } from 'react';

import { BaseText } from '../../common/BaseText';
import type { ITooltipProps } from './Tooltip';

export enum TooltipPositionEnum {
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
  Left = 'left',
  TopLeft = 'top-left',
  TopRight = 'top-right',
}

interface IPositionContainerStyleProps {
  isRendered: boolean;
}

const PositionContainer = styled(View)(
  (props: IPositionContainerStyleProps) => ({
    opacity: props.isRendered ? 1 : 0,
    position: 'absolute',
  }),
);

const ContentContainer = styled(View)({
  borderRadius: 12,
  overflow: 'hidden',
  px: '$5',
  py: '$4',
  justifyContent: 'center',
  alignItems: 'center',
});

const Background = styled(View)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  bg: '$background0',
  opacity: 0.4,
  zIndex: -1,
});

const Text = styled(BaseText)({
  color: '$background0',
  textAlign: 'center',
});

interface ITooltipContentProps
  extends Omit<ITooltipProps, 'children'> {
  targetRef: RefObject<View>;
  onClose: () => void;
}

export function TooltipContent({
  targetRef,
  onClose,
  offset = 0,
  position = TooltipPositionEnum.Top,
  text,
}: ITooltipContentProps) {
  const overlayRef = useRef<View>(null);
  const placement = position.replace('-', ' ') as Placement;

  // Get tooltip content positioning props relative to the target
  const { overlayProps, rendered } = useOverlayPosition({
    targetRef,
    overlayRef,
    offset,
    placement,
    containerPadding: 0,
    onClose,
  });

  return (
    <PositionContainer
      ref={overlayRef}
      collapsable={false}
      isRendered={rendered}
      style={overlayProps.style}
    >
      <ContentContainer>
        <Background />
        <Text>{text}</Text>
      </ContentContainer>
    </PositionContainer>
  );
}
